import { useMemo } from 'react';

import { useWorkspaceStore } from '@desygner/ui-data-access-workspace';
import { useProjectsStore } from '@desygner/ui-features-projects';

import { useConfigStore, useUserStore, useViewerStore } from '../../../../../../store';
import { ProjectAction } from '../../../../../../store/viewer/viewer.types';
import { AureliaShareModal } from './AureliaShareModal/AureliaShareModal';
import { ShareWithAccessMenu } from './ShareWithAccessMenu/ShareWithAccessMenu';
import { SimpleShare } from './SimpleShare/SimpleShare';

export const Share = ({ onRequestApproval }: { onRequestApproval: () => void }) => {
  const isPublic = useProjectsStore((state) => state.project.currentProject?.is_public ?? false);
  const userId = useUserStore((state) => state.userId);
  const projectType = useViewerStore((state) => state.project.type);
  const collaborators = useViewerStore((state) => state.collaborators);
  const isOwner = useViewerStore((state) => state.project.isOwner);
  const feature = useConfigStore((state) => state.feature);
  const isShareActionRequireApproval = useViewerStore(
    (state) => state.isProjectActionRequireApproval[ProjectAction.Share]
  );
  const isShareActionReady = useWorkspaceStore((state) =>
    state.workflow.isActionReady(ProjectAction.Share)
  );
  const isShareActionPending = useWorkspaceStore((state) =>
    state.workflow.isActionPending(ProjectAction.Share)
  );

  const hasCollaborationPermissions = useMemo(
    () =>
      collaborators?.some(
        (collaborator) =>
          collaborator.permissionUserId === userId && collaborator.permissionType !== 'owner'
      ),
    [collaborators, userId]
  );

  const isCollaborativePrivateTemplate = useMemo(
    () => projectType === 'template' && !isPublic,
    [projectType, isPublic]
  );

  const shouldShowShareWithAccessMenu = useMemo(
    () => isPublic || hasCollaborationPermissions || isOwner || isCollaborativePrivateTemplate,
    [isPublic, hasCollaborationPermissions, isOwner, isCollaborativePrivateTemplate]
  );

  const isApprovalRequired = useMemo(() => {
    return isShareActionRequireApproval && !isShareActionReady;
  }, [isShareActionRequireApproval, isShareActionReady]);

  if (feature.legacyShareModal) {
    return (
      <AureliaShareModal
        isApprovalRequired={isApprovalRequired}
        onRequestApproval={onRequestApproval}
      />
    );
  }

  if (shouldShowShareWithAccessMenu) {
    return <ShareWithAccessMenu />;
  }

  return <SimpleShare />;
};
